import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import authAPI from '../../services/authAPI';
import { User, LoginRequest, RegisterRequest, SendOtpRequest, VerifyOtpLoginRequest, VerifyOtpRegisterRequest } from '../../types/api';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  loading: false,
  error: null,
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await authAPI.login(credentials);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Login failed');
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData: RegisterRequest, { rejectWithValue }) => {
    try {
      const response = await authAPI.register(userData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Registration failed');
    }
  }
);

export const sendOtp = createAsyncThunk(
  'auth/sendOtp',
  async (payload: SendOtpRequest, { rejectWithValue }) => {
    try {
      return await authAPI.sendOtp(payload);
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      return rejectWithValue(
        typeof detail === 'string' ? detail : detail?.[0]?.msg || 'Failed to send verification code'
      );
    }
  }
);

export const verifyOtpRegister = createAsyncThunk(
  'auth/verifyOtpRegister',
  async (payload: VerifyOtpRegisterRequest, { rejectWithValue }) => {
    try {
      return await authAPI.verifyOtpRegister(payload);
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      return rejectWithValue(
        typeof detail === 'string' ? detail : detail?.[0]?.msg || 'Verification failed'
      );
    }
  }
);

export const verifyOtpLogin = createAsyncThunk(
  'auth/verifyOtpLogin',
  async (payload: VerifyOtpLoginRequest, { rejectWithValue }) => {
    try {
      return await authAPI.verifyOtpLogin(payload);
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      return rejectWithValue(
        typeof detail === 'string' ? detail : detail?.[0]?.msg || 'Verification failed'
      );
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authAPI.getCurrentUser();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to get user info');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (userData: Partial<User>, { rejectWithValue }) => {
    try {
      const response = await authAPI.updateProfile(userData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Profile update failed');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authAPI.logout();
      return true;
    } catch (error: any) {
      // Even if API call fails, we'll logout locally
      localStorage.removeItem('token');
      // Don't reject - we want to logout locally regardless
      return true;
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      state.isAuthenticated = true;
      localStorage.setItem('token', action.payload);
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      localStorage.removeItem('token');
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.data.access_token;
        state.user = action.payload.data.user;
        state.isAuthenticated = true;
        state.error = null;
        // Store token in localStorage
        if (action.payload.data?.access_token) {
          localStorage.setItem('token', action.payload.data.access_token);
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      })
      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Send OTP
      .addCase(sendOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendOtp.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(sendOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Verify OTP register
      .addCase(verifyOtpRegister.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtpRegister.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.data.access_token;
        state.user = action.payload.data.user;
        state.isAuthenticated = true;
        state.error = null;
        if (action.payload.data?.access_token) {
          localStorage.setItem('token', action.payload.data.access_token);
        }
      })
      .addCase(verifyOtpRegister.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Verify OTP login
      .addCase(verifyOtpLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtpLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.data.access_token;
        state.user = action.payload.data.user;
        state.isAuthenticated = true;
        state.error = null;
        if (action.payload.data?.access_token) {
          localStorage.setItem('token', action.payload.data.access_token);
        }
      })
      .addCase(verifyOtpLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Get Current User
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // If token is invalid, logout
        state.token = null;
        state.isAuthenticated = false;
        localStorage.removeItem('token');
      })
      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Logout
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = null;
        localStorage.removeItem('token');
      })
      .addCase(logoutUser.rejected, (state) => {
        // Even if logout fails, clear local state
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = null;
        localStorage.removeItem('token');
      });
  },
});

export const { clearError, setToken, logout, setUser } = authSlice.actions;
export default authSlice.reducer; 